// frontend/src/components/common/ActiveFilterBar.jsx
import { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Pill from './Pill';
import ResultCount from './ResultCount';
import useOverflowMask from './useOverflowMask';
import { FS, SP, TOUCH_TARGET_COARSE } from '../../theme/tokens';

/** Der Schluessel eines Chips — derselbe Ausdruck wie sein React-`key` unten. */
const schluessel = (c) => c.id ?? c.label;

/**
 * ActiveFilterBar — Z3: the ONE row that reads back the whole query (BS3, G0.10).
 *
 * @description
 *   „Filtered by:  [Status: Open, Waiting ✕]  [Overdue ✕]  [Search: burr ✕]     Clear all"
 *   (Konzept `tabellen-filter-suche.md` §4.1). It is the RECEIPT of the filter row
 *   (Z1/Z2), not a second set of controls: every narrowing criterion of the query
 *   is ONE chip with its ✕, plus a „Clear all" at the right edge.
 *
 *   🔴 **The content is `useListQuery().chips`** (BS1) — the same contract
 *   `query.chips` hands over: `[{ id, label, isDefault, onRemove }]`. The label
 *   already carries the value AND the `· default` suffix (built in
 *   `useListQuery.js` `baueChips`); this component renders it, it does NOT
 *   re-word it. One chip per criterion, including defaults (§4.1/N4.2) — a
 *   hidden default is the defect P28 calibrates against.
 *
 *   🔴 **It appears as soon as ANYTHING narrows the list** (§4.1: „Sie fehlt nur,
 *   wenn die Abfrage nichts davon trägt"), i.e. on `chips.length > 0` — NOT on
 *   `isDefault` (the frame segment moves `isDefault` without producing a chip,
 *   `useListQuery` @returns). On the PHONE it additionally renders for the
 *   counter alone (N11.10: „Ohne Einengung steht nur der Zähler") — there the
 *   count lives in Z3, not in Z1.
 *
 *   🔴 **One line by default, and it does not wrap by itself** (§4.1): the chip
 *   strip scrolls horizontally (`overflowX: 'auto'`, scrollbar hidden).
 *   🔴 **Nur mit `mobilUmbruch` bricht die ZEILE auf dem Telefon um** (G11c,
 *   2026-10-30): Zähler und „Clear all" bleiben in Zeile 1, die Chipzeile
 *   bekommt die volle Breite in Zeile 2, und ein breiter Vorgabe-Chip wird nicht
 *   mehr angeschnitten. Die Chipstrecke DARIN rollt weiter — der Umbruch ersetzt
 *   das Rollen nicht. ⚠️ **Die Vorgabe ist `false`, und das ist gemessen:** ein
 *   Umbruch auf JEDER mobilen Liste kostet eine Zeile über der Tabelle und riss
 *   in der Kette `g11cc` die Hauszusagen P2 (`/fulfillment`: die erste
 *   Datenzeile stand bei 853 px in einem 844-px-Bildschirm) und M1 (`/products`:
 *   der 44-px-Boden des Marken-Links verlor sein 43-px-Punkt). Er ist deshalb
 *   ein **Opt-in des Aufrufers**, keine neue Hausform. 🔴 **The soft edge at a
 *   really overflowing end comes from `useOverflowMask` (BS15)** — the SAME
 *   implementation `UnderlineTabs` (BS11) and `TableViewport` (BS14) wear, not a
 *   fourth hand-rolled copy. That is the promise the registry already made
 *   (`component-registry.md`, „Z3-Chipzeile" among the users of BS15).
 *
 *   ⚠️ **The concept contradicts itself here, and this line resolves it:** §4.1
 *   (`tabellen-filter-suche.md:337`) prescribes for Z3 only `overflowX: 'auto'`
 *   and names no mask, while §12 BS15 (`:886`) names „Reiter, **Z2, Z3** und
 *   `TableViewport`" as the users of the ONE mask. **Both hold** — `overflowX`
 *   is the scrolling itself, the mask is the hint at a cut-off edge; §4.1 names
 *   the mechanism, BS15 names the implementation. Without the mask a clipped row
 *   looks complete (the seventh chip is simply gone), which is exactly the
 *   defect BS15 exists to end.
 *
 *   🔴 **The cap is a DESKTOP rule only.** Beyond `maxVisible` chips the desktop
 *   row shows the first `maxVisible` plus a „+N more" **text button** at the
 *   right edge, which unfolds the row to several lines (R16-B1: on the PHONE
 *   there is NO cap — the row simply scrolls, N11.10 „danach die Chips in einer
 *   rollenden Zeile"). A cap on the phone would make chips 6…n **unreachable**:
 *   the only opener („+N more") lives in the desktop branch, so those criteria
 *   could be neither read nor removed — and Z3 is „der eine Ort, an dem man den
 *   **vollständigen** Zustand der Abfrage liest" (§4.1).
 *
 *   🔴 **„Show less" steht nur, solange es etwas zu verbergen gibt:** fällt die
 *   Chipzahl nach dem Aufklappen wieder auf `maxVisible` oder darunter (etwa
 *   durch ✕), klappt die Zeile STILL zurück und der Knopf verschwindet — sonst
 *   sagte er „weniger zeigen", während nichts mehr verdeckt ist (R16-Beobachtung
 *   2026-10-12).
 *
 *   🔴 **„+N more"/„Show less" are TEXT BUTTONS, not chips** — the one place
 *   where this component knowingly departs from the concept's wording (§4.1
 *   `:337` calls „+n more" a „Chip"). It is a HANDLING, not a criterion: it
 *   carries no filter value, and „Clear all" next to it is a text button for
 *   exactly the same reason (§4.1 `:338`). A `Pill` would promise a removable
 *   criterion that is not there.
 *
 *   🔴 **The threshold is a NUMBER here, not a width measurement:** jsdom has no
 *   layout, so an overflow test could not be driven (Regel 17 §3b). The browser
 *   measurement belongs to the Musterroute (G0.11, P4/P6); this component states
 *   its rule so that rule is testable at all. The same holds for the mask: what
 *   is proven here is that the mask SX is emitted once the box reports overflow —
 *   not how many pixels the row really needs.
 *
 *   🔴 **Mobile order is different, and it is the whole point of §11.3/N11.10:**
 *   the counter comes FIRST, then „Clear all", then the rolling chip row — a
 *   phone reader sees „42 of 312 · Clear all" before the chips, because the
 *   chips scroll and the two fixed items must not.
 *
 *   🔴 **Every chip's ✕ is a real button** (§4.1: „Fläche, die sich als Knopf
 *   ausgibt, ist ein Knopf") with the name „Remove filter <Text>" — so the ✕ of a
 *   chip WITHOUT text has no name, and this component THROWS on a chip without a
 *   `label` (house form `EmptyState.reason`). It renders `Pill` (BS-agnostisch,
 *   Regel 22 §Component rules 1) and no MUI `Chip`.
 *
 *   🔴 **Focus comes back** (§4.3, house rule „wer sich mit seiner eigenen
 *   Handlung entfernt, gibt den Fokus zurück"): removing a chip's ✕ hands the
 *   focus to the NEXT chip's ✕, or — when none follows — to the PREVIOUS one; if
 *   no chip remains at all, and after „Clear all", the focus goes to the search
 *   field (`returnFocusRef`, the same sink shape `BulkActionBar` uses). The move
 *   runs INSIDE the click handler, i.e. BEFORE React detaches the row: on the
 *   desktop the whole bar unmounts once the last chip is gone, and a focus set
 *   afterwards would land on `document.body`.
 *
 *   🔴 **The „previous chip" is a KNOWING addition to §4.3** — the same house
 *   form as the text-button departure above. §4.3 (`tabellen-filter-suche.md:366`)
 *   names only „den **nächsten** Chip oder, wenn keiner mehr steht, das Suchfeld";
 *   it names NO previous chip. The third step is added here on purpose: it keeps
 *   the focus IN the row, instead of throwing it across half the page into the
 *   search field. It fires only when the removed chip was the LAST one AND chips
 *   still stand — the case §4.3 does not rule (R15-W3).
 *
 *   🔴 **No number, no colour literal** (Regel 22): `FS`, `SP`, the `Pill` shape.
 *   The „Filtered by:" label is `text.secondary`/`FS.sm` (§4.1), the chips are
 *   `Pill size="sm"`.
 *
 * @useWhen
 *   - Z3 of a list view, fed by `useListQuery().chips` (BS1) — the house form
 *   - A view that has to say WHICH criteria narrow it, defaults included
 *   - The mobile counter home (N11.10) when Z1 is only search + „Filters"
 *
 * @dontUseFor
 *   - The filter CONTROLS themselves — that is `FilterPillRow` (Z2), `FilterSheet`
 *     (the „Filters" button) or the facet pills
 *   - A status/result chip row — that is `Pill` directly (a different question)
 *   - The one result counter of a list — that is `ResultCount` (BS4); this bar
 *     only carries it into Z3 on the phone
 *
 * @related
 *   - `useListQuery` (BS1) — supplies `chips` (`{id, label, isDefault, onRemove}`)
 *     and `clearAll`
 *   - `ListToolbar` (BS2) — renders this bar as Z3 (`liste-z3`) and wires
 *     `returnFocusRef` to its search field
 *   - `ResultCount` (BS4) — the one `role="status"` counter, rendered here mobile
 *   - `Pill` — the one chip shape
 *   - `useOverflowMask` (BS15) — the soft edge on the rolling chip row
 *
 * @reuseHints
 *   - Every list migration (G1–G15) replaces its hand-rolled active-filter row
 *   - A report filter bar with a read-back row can drop onto the same contract
 *
 * @props
 *   chips       — `[{ id, label, isDefault, onRemove }]` (from `useListQuery`).
 *                 🔴 A chip WITHOUT `label` THROWS: the text is the accessible
 *                 name of its ✕ („Remove filter <Text>").
 *   onClearAll  — the „Clear all" handler. Omitted, the button is not drawn.
 *   count       — `{ n, m, noun, unknownTotal, unknown, loading }` (a `ResultCount`
 *                 config). 🔴 MOBILE ONLY: on the phone Z3 begins with the
 *                 counter (§11.3/N11.10); on the desktop the counter stays in Z1
 *                 (§2.1) and this prop is not read.
 *   kompakt     — `false` (default) | `true`: the caller's Z1 is in its compact
 *                 form at a width where the viewport query still says desktop
 *                 (`ListToolbar.kompakt`, `/overview` at 768 px). Then the
 *                 counter is at home HERE, exactly as on the phone — it must
 *                 not vanish just because the window is 768 px wide.
 *                 🔴 Still ONE counter per list: `ListToolbar` drops it from Z1
 *                 whenever it passes `kompakt`.
 *   maxVisible  — how many chips stand on one line before „+N more" (default 5).
 *                 🔴 DESKTOP ONLY — the phone renders every chip (R16-B1)
 *   mobilUmbruch — `false` (default) | `true`: on the phone the Z3 row may wrap
 *                 to a second line instead of clipping a wide chip. `false`
 *                 keeps the row byte-identical to the pre-G11c form (one line,
 *                 chip strip scrolls). See the description above for the
 *                 measured cost of `true`.
 *   returnFocusRef — optional `React.Ref` to the element that takes the focus
 *                 when no chip is left (the search field, §4.3). Without it the
 *                 focus simply stays where it was
 *   sx          — last applied, the caller wins
 */
export default function ActiveFilterBar({
  chips = [],
  onClearAll,
  count,
  kompakt = false,
  maxVisible = 5,
  mobilUmbruch = false,
  returnFocusRef,
  sx = {},
}) {
  const theme = useTheme();
  // 🔴 Zwei Begriffe: `schmal` ist die Fensterbreite, `mobil` die FORM dieser
  // Zeile — sie gilt zusätzlich, wenn der Aufrufer seine Z1 kompakt stellt
  // (`ListToolbar.kompakt`, R15/Fotobefund 768 px). Der Zähler darf nicht
  // verschwinden, nur weil das Fenster 768 px breit ist.
  const schmal = useMediaQuery(theme.breakpoints.down('sm'));
  const mobil = schmal || kompakt;
  const [ausgeklappt, setAusgeklappt] = useState(false);
  // 🔴 Aufgeklappt ist nur, solange es etwas zu VERBERGEN gibt (R16-Beobachtung
  // 2026-10-12): faellt die Chipzahl unter `maxVisible`, klappt die Zeile still
  // zurueck und „Show less" verschwindet. Ohne diese Ableitung stuende der Knopf
  // ohne Gegenstand — „weniger zeigen", waehrend nichts mehr verdeckt ist. Die
  // Schwelle ist dieselbe wie die des Deckels (`maxVisible`, Desktop), und der
  // Ruhezustand bleibt erhalten: waechst die Zeile wieder ueber die Schwelle,
  // ist sie noch aufgeklappt.
  const aufgeklappt = ausgeklappt && chips.length > maxVisible;

  // Die rollende Chipzeile und ihre weiche Kante (BS15). 🔴 Die Haken stehen VOR
  // dem fruehen `return null` unten — ein Haken nach einem Ausstieg waere ein
  // Verstoss gegen die Aufrufordnung, und die Zeile haengt in beiden Breiten.
  const rollRef = useRef(null);
  // Der Nachlauf des Hooks: der `ResizeObserver` sieht nur die BOX, nicht den
  // Inhalt — ein Chip mehr oder das Aufklappen aendert die Box nicht.
  const { maskSx } = useOverflowMask(rollRef, { deps: [chips.length, aufgeklappt] });
  // Die ✕-Knoepfe je Chip, damit der Fokus nach dem Entfernen weiterwandern kann
  // (§4.3). Geschluesselt wie der Chip selbst (`id ?? label`).
  const kreuzeRef = useRef({});

  // 🔴 „Show less" entfernt sich beim Klick SELBST — der Fokus fiele auf
  // `document.body` (R16-Warnung 2026-10-12). Ziel ist „+N more": derselbe Platz,
  // dieselbe Handlung in der Gegenrichtung. 🔴 Anders als beim ✕ existiert der
  // Zielknoten beim Klick NOCH NICHT — er entsteht erst mit dem Zuklappen, deshalb
  // wird er NACH dem Rendern fokussiert (Effekt), nicht im Handler.
  const mehrRef = useRef(null);
  const fokusNachMehrRef = useRef(false);
  // Der Effekt laeuft NACH dem Rendern — dann steht „+N more" im Baum. Die Marke
  // unterscheidet „der Nutzer hat zugeklappt" von jedem anderen Weg, auf dem
  // `aufgeklappt` falsch wird (etwa ein ✕, das die Chipzahl unter die Schwelle
  // drueckt) — dort bleibt der Fokus, wo der ✕-Handler ihn hingesetzt hat.
  useEffect(() => {
    if (aufgeklappt || !fokusNachMehrRef.current) return;
    fokusNachMehrRef.current = false;
    mehrRef.current?.focus?.();
  }, [aufgeklappt]);

  // 🔴 Pflichtname wirft (Hausform `EmptyState.reason`, `MobileRecordCard.selectable`):
  // der Text eines Chips ist zugleich der zugängliche Name seines ✕-Knopfes.
  // Ein Chip ohne Text ist keine Auskunft — und sein ✕ wäre ein namenloser Knopf.
  chips.forEach((c, i) => {
    if (!c || typeof c.label !== 'string' || c.label === '') {
      throw new Error(
        `ActiveFilterBar: der Chip an Stelle ${i} hat kein \`label\` — der Text ist ` +
          'zugleich der zugängliche Name seines ✕-Knopfes („Remove filter <Text>"). ' +
          'Ein Chip ohne Text ist keine Auskunft und sein ✕ ein namenloser Knopf.',
      );
    }
  });

  const hatChips = chips.length > 0;
  const mitZaehler = Boolean(count);
  // §4.1: Z3 steht, sobald etwas einengt. 🔴 Zusaetzlich mobil fuer den Zaehler
  // ALLEIN (N11.10: „Ohne Einengung steht nur der Zaehler („312 cases")") — dort
  // wohnt der Zaehler in Z3, nicht in Z1.
  if (!hatChips && !(mobil && mitZaehler)) return null;

  // 🔴 Der Deckel gilt NUR am Desktop (R16-B1). Mobil rollt die Zeile ohnehin
  // (§11.3/N11.10), und ein Deckel waere dort unerreichbar: der einzige Oeffner
  // („+N more") sitzt im Desktop-Zweig — die Chips 6…n koennten weder gelesen
  // noch einzeln entfernt werden.
  const sichtbar = hatChips
    ? (mobil || aufgeklappt ? chips : chips.slice(0, maxVisible))
    : [];
  const verdeckt = !mobil && hatChips ? Math.max(0, chips.length - maxVisible) : 0;

  // 🔴 Fokus nach dem Entfernen (§4.3): der NAECHSTE Chip, sonst der VORIGE,
  // sonst das Suchfeld. Der Zielknoten wird VOR `onRemove` bestimmt — auf dem
  // Desktop haengt die ganze Leiste ab, sobald der letzte Chip weg ist, und ein
  // danach gesetzter Fokus faellt auf `document.body`.
  const entferne = (c, i) => {
    const ids = sichtbar.map(schluessel);
    const naechster = ids[i + 1] ?? ids[i - 1];
    const ziel = naechster != null ? kreuzeRef.current[naechster] : returnFocusRef?.current;
    c.onRemove?.();
    ziel?.focus?.();
  };

  // „Clear all" → Grundzustand; der Fokus geht ins Suchfeld (§4.3). Auch hier
  // VOR dem moeglichen Abbau der Leiste.
  const allesZurueck = () => {
    onClearAll?.();
    returnFocusRef?.current?.focus?.();
  };

  // Ein Chip = `Pill` (Regel 22 §1: EIN Chip-Bauteil) mit dem ✕ als ECHTEM Knopf
  // darin (§4.1). Der Name des Knopfes traegt den Chip-Text („Remove filter …"),
  // damit ein Screenreader bei mehreren Chips weiss, WELCHER entfernt wird.
  const chipKnoten = (c, i) => (
    <Pill key={schluessel(c)} size="sm" nowrap sx={{ flexShrink: 0, pr: SP.chip }}>
      {c.label}
      <IconButton
        size="small"
        ref={(el) => {
          if (el) kreuzeRef.current[schluessel(c)] = el;
          else delete kreuzeRef.current[schluessel(c)];
        }}
        aria-label={`Remove filter ${c.label}`}
        onClick={() => entferne(c, i)}
        sx={{ p: SP.chip, ml: SP.chip, color: 'inherit', ...TOUCH_TARGET_COARSE }}
      >
        <CloseIcon sx={{ fontSize: FS.base }} />
      </IconButton>
    </Pill>
  );

  // Die rollende Chipzeile. `overflowX: 'auto'` + versteckte Bildlaufleiste ist
  // die Hausform aus `FilterPillRow`; ausgeklappt bricht sie stattdessen um
  // (§4.1: „+n more … klappt Z3 zu mehreren Zeilen auf") — dann gibt es keine
  // Kante zu zeigen, also auch keine Maske.
  const chipzeile = (
    <Box
      ref={rollRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: SP.tight,
        minWidth: 0,
        // 🔴 G11c-Nachbesserung 2 (2026-10-31): der Umbruch ist ein OPT-IN
        // (`mobilUmbruch`), kein neuer Hauswert. Mit `flex: '1 1 auto'` richtet
        // sich die Grundbreite nach dem INHALT statt nach 0 — passt der Chip
        // dann nicht mehr neben Zaehler und „Clear all", rutscht die Zeile in
        // die naechste und bekommt die volle Breite (Foto `390_themen-grund`).
        // 🔴 Ohne das Opt-in bleibt es bei `flex: 1`: ein Umbruch auf JEDER
        // mobilen Liste kostete eine Zeile und riss P2/M1 der Kette `g11cc`.
        ...(mobil && mobilUmbruch ? { flex: '1 1 auto' } : { flex: 1 }),
        ...(aufgeklappt
          ? { flexWrap: 'wrap' }
          : {
              flexWrap: 'nowrap',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              // 🔴 BS15: die weiche Kante an dem Rand, an dem wirklich etwas
              // weitergeht. Leer, solange nichts uebersteht — „keine Kante" ist
              // die richtige Aussage, nicht „unbekannt".
              ...maskSx,
            }),
      }}
    >
      {sichtbar.map(chipKnoten)}
    </Box>
  );

  if (mobil) {
    // §11.3/N11.10: Zaehler ZUERST, dann „Clear all", dann die rollenden Chips.
    // Beide festen Teile stehen AUSSERHALB der Rollflaeche — ein Filterweg, den
    // man erst herbeiwischen muss, ist der Befund T3-12.
    return (
      <Box
        data-testid="liste-z3"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: SP.tight,
          minWidth: 0,
          // 🔴 G11c-Nachbesserung 2 (2026-10-31): UMBRECHEN nur auf Wunsch des
          // Aufrufers (`mobilUmbruch`). Zaehler und „Clear all" sind feste Teile
          // (T3-12); passt der Chip nicht mehr daneben, steht er dann in der
          // naechsten Zeile — vollstaendig lesbar, statt am Rand abgeschnitten
          // zu werden. Ohne das Opt-in bleibt die Zeile einzeilig (Vorgabe).
          ...(mobilUmbruch ? { flexWrap: 'wrap' } : {}),
          ...sx,
        }}
      >
        {mitZaehler && (
          <Box sx={{ flexShrink: 0 }}>
            <ResultCount {...count} />
          </Box>
        )}
        {hatChips && onClearAll && (
          <Button size="small" onClick={allesZurueck} sx={{ flexShrink: 0 }}>
            Clear all
          </Button>
        )}
        {chipzeile}
      </Box>
    );
  }

  // Desktop: „Filtered by:" · Chips · „+N more" · „Clear all" rechts (§2.1/§4.1).
  // „+N more"/„Show less" sind TEXTKNOEPFE, kein Chip: eine Handlung, kein
  // Kriterium (s. JSDoc).
  return (
    <Box
      data-testid="liste-z3"
      sx={{ display: 'flex', alignItems: 'center', gap: SP.tight, minWidth: 0, ...sx }}
    >
      <Typography
        component="span"
        sx={{ fontSize: FS.sm, color: 'text.secondary', flexShrink: 0 }}
      >
        Filtered by:
      </Typography>
      {chipzeile}
      {verdeckt > 0 && !aufgeklappt && (
        <Button
          size="small"
          ref={mehrRef}
          onClick={() => setAusgeklappt(true)}
          sx={{ flexShrink: 0 }}
        >
          {`+${verdeckt} more`}
        </Button>
      )}
      {aufgeklappt && (
        <Button
          size="small"
          onClick={() => {
            // Der Knopf entfernt sich selbst: die Marke traegt den Fokus an den
            // Nachfolger an derselben Stelle (s. Effekt oben).
            fokusNachMehrRef.current = true;
            setAusgeklappt(false);
          }}
          sx={{ flexShrink: 0 }}
        >
          Show less
        </Button>
      )}
      {hatChips && onClearAll && (
        <Button size="small" onClick={allesZurueck} sx={{ flexShrink: 0 }}>
          Clear all
        </Button>
      )}
    </Box>
  );
}
